<template>
  <slot name="trigger" :show="show" :is-open="model" :close="close" />
  <Teleport to="body">
    <Transition :name="transitionAnimation">
      <div v-if="model" class="ui-overlay" @click="onOverlayClick">
        <div class="ui-overlay__backdrop" />
        <div class="ui-overlay__overlay" @click.stop>
          <div class="ui-overlay__content">
            <slot :is-open="model" :close="close" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { lockBodyScroll, unlockBodyScroll } from '@/utils/body'
import { watch, onMounted, onBeforeUnmount } from 'vue'

export interface UiOverlayProps {
  title?: string
  closeOnOverlayClick?: boolean
  closeOnEsc?: boolean
  transitionAnimation?: string
}

const props = withDefaults(defineProps<UiOverlayProps>(), {
  closeOnOverlayClick: true,
  closeOnEsc: true,
})

const model = defineModel<boolean>()

// Events
const emit = defineEmits<{
  open: []
  close: []
}>()

// Methods
function show() {
  model.value = true
}

function close() {
  model.value = false
}

function onOverlayClick() {
  if (props.closeOnOverlayClick) {
    close()
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.closeOnEsc) {
    close()
  }
}

// Watchers
watch(
  () => model,
  (isOpen) => {
    if (isOpen) {
      emit('open')
      lockBodyScroll()
    } else {
      emit('close')
      unlockBodyScroll()
    }
  },
)

// Hooks
onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  unlockBodyScroll()
})
</script>

<style lang="sass" scoped>
@use '@/styles/variables'
@use 'sass:map'

.ui-overlay
  position: fixed
  inset: 0
  z-index: 9999
  display: flex
  align-items: center
  justify-content: center
  padding: variables.$spacer * 3

  &__backdrop
    position: absolute
    display: block
    width: 100%
    height: 100%
    background-color: map.get(variables.$background-color, 'tertiary')
    backdrop-filter: blur(1px)
    z-index: -1
    pointer-events: auto
    opacity: 0.3

  &__content
    overflow-y: auto
    flex: 1 1 auto
</style>
